import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Grid,
  Paper,
  TextField,
  Typography,
  Autocomplete,
  Button,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Toolbar
} from "@mui/material";
import { BASE_URL } from "../../../global";
import axiosWithToken from "../../../utils/components/axiosTokenConfig";
import EditWrap from "../../commonComponent/editWrap";

export const gaugeTypeOptions = [
  { id: 1, label: "Plain Plug" },
  { id: 2, label: "Snap Gauge" }
];

export default function EditGauge(props) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id && id !== "0");

  const [gaugeType, setGaugeType] = useState(null);
  const [specification, setSpecification] = useState("");
  const [goNominalSize, setGoNominalSize] = useState("");
  const [goMax, setGoMax] = useState("");
  const [goMin, setGoMin] = useState("");
  const [noGoNominalSize, setNoGoNominalSize] = useState("");
  const [noGoMax, setNoGoMax] = useState("");
  const [noGoMin, setNoGoMin] = useState("");
  const [wearLimit, setWearLimit] = useState("");

  useEffect(() => {
    if (isEditing) {
      fetchDetails();
    }
  }, [id]);

  const fetchDetails = async () => {
    try {
      const res = await axiosWithToken.post(`${BASE_URL}dynamic`, {
        query: `SELECT * FROM plainSnapGaugeSize WHERE id = ${id}`,
      });
      const data = res.data?.[0];
      if (data) {
        const foundType = gaugeTypeOptions.find((opt) => opt.id === Number(data.type)) || null;
        setGaugeType(foundType);
        setSpecification(data.specification || "");
        setGoNominalSize(data.goNominalSize !== null && data.goNominalSize !== undefined ? data.goNominalSize : "");
        setGoMax(data.goMax !== null && data.goMax !== undefined ? data.goMax : "");
        setGoMin(data.goMin !== null && data.goMin !== undefined ? data.goMin : "");
        setNoGoNominalSize(data.noGoNominalSize !== null && data.noGoNominalSize !== undefined ? data.noGoNominalSize : "");
        setNoGoMax(data.noGoMax !== null && data.noGoMax !== undefined ? data.noGoMax : "");
        setNoGoMin(data.noGoMin !== null && data.noGoMin !== undefined ? data.noGoMin : "");
        setWearLimit(data.wearLimit !== null && data.wearLimit !== undefined ? data.wearLimit : "");
      }
    } catch (error) {
      console.error("Error fetching Plain Plug / Snap Gauge details:", error);
      toast.error("Failed to load details");
    }
  };

  const sanitizeVal = (val) => {
    if (val === "" || val === null || val === undefined || isNaN(val)) return "NULL";
    return parseFloat(val);
  };

  const escapeSql = (str) => {
    if (!str) return "";
    return str.replace(/[\0\x08\x09\x1a\n\r"'\\\%]/g, (char) => {
      switch (char) {
        case "\0": return "\\0";
        case "\x08": return "\\b";
        case "\x09": return "\\t";
        case "\x1a": return "\\z";
        case "\n": return "\\n";
        case "\r": return "\\r";
        case "\"":
        case "'":
        case "\\":
        case "%": return "\\" + char;
        default: return char;
      }
    });
  };

  const handleSubmit = async () => {
    if (!gaugeType) {
      toast.error("Please select a Gauge Type");
      return;
    }
    if (!specification.trim()) {
      toast.error("Please enter Standard Specification");
      return;
    }

    const typeVal = gaugeType.id;
    const specVal = escapeSql(specification.trim());
    const gNom = sanitizeVal(goNominalSize);
    const gMax = sanitizeVal(goMax);
    const gMin = sanitizeVal(goMin);
    const ngNom = sanitizeVal(noGoNominalSize);
    const ngMax = sanitizeVal(noGoMax);
    const ngMin = sanitizeVal(noGoMin);
    const wLimit = sanitizeVal(wearLimit);

    try {
      if (isEditing) {
        const updateQuery = `UPDATE plainSnapGaugeSize SET type = ${typeVal}, specification = '${specVal}', goNominalSize = ${gNom}, goMax = ${gMax}, goMin = ${gMin}, noGoNominalSize = ${ngNom}, noGoMax = ${ngMax}, noGoMin = ${ngMin}, wearLimit = ${wLimit} WHERE id = ${id}`;
        await axiosWithToken.post(`${BASE_URL}dynamic`, { query: updateQuery });
        toast.success("Plain Plug / Snap Gauge updated successfully!");
      } else {
        const insertQuery = `INSERT INTO plainSnapGaugeSize (type, specification, goNominalSize, goMax, goMin, noGoNominalSize, noGoMax, noGoMin, wearLimit, status) VALUES (${typeVal}, '${specVal}', ${gNom}, ${gMax}, ${gMin}, ${ngNom}, ${ngMax}, ${ngMin}, ${wLimit}, 1)`;
        await axiosWithToken.post(`${BASE_URL}dynamic`, { query: insertQuery });
        toast.success("Plain Plug / Snap Gauge created successfully!");
      }
      setTimeout(() => {
        navigate("/master/plainPlugSnapGaugeList");
      }, 500);
    } catch (error) {
      console.error("Error saving Plain Plug / Snap Gauge:", error);
      toast.error("Failed to save");
    }
  };

  return (
    <EditWrap>
      {/* Page Title */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1.5rem" }}>
        <Typography variant="h6" component="h6" style={{ float: "left" }}>
          {isEditing ? "Edit Plain Plug / Snap Gauge" : "Create New Plain Plug / Snap Gauge"}
        </Typography>
      </div>

      {/* Form Controls */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Autocomplete
            size="small"
            options={gaugeTypeOptions}
            getOptionLabel={(option) => option.label || ""}
            value={gaugeType}
            onChange={(e, newValue) => setGaugeType(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Gauge Type *" variant="outlined" />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={4}>
          <TextField
            label="Standard Specification *"
            variant="outlined"
            size="small"
            fullWidth
            value={specification}
            onChange={(e) => setSpecification(e.target.value)}
          />
        </Grid>
      </Grid>

      {/* Limits Table */}
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, borderRadius: 1 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#DDDDDD" }}>
            <TableRow>
              <TableCell sx={{ width: "25%" }}>
                <b>PARAMETER</b>
              </TableCell>
              <TableCell align="center" sx={{ width: "25%" }}>
                <b>NOMINAL SIZE (MM)</b>
              </TableCell>
              <TableCell align="center" sx={{ width: "25%" }}>
                <b>SPECIFIED LIMIT MAX (MM)</b>
              </TableCell>
              <TableCell align="center" sx={{ width: "25%" }}>
                <b>SPECIFIED LIMIT MIN (MM)</b>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* GO */}
            <TableRow>
              <TableCell sx={{ fontWeight: "600" }}>GO</TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={goNominalSize}
                  onChange={(e) => setGoNominalSize(e.target.value)}
                  sx={{ width: "80%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={goMax}
                  onChange={(e) => setGoMax(e.target.value)}
                  sx={{ width: "80%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={goMin}
                  onChange={(e) => setGoMin(e.target.value)}
                  sx={{ width: "80%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
            </TableRow>

            {/* NOGO */}
            <TableRow>
              <TableCell sx={{ fontWeight: "600" }}>NOGO</TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={noGoNominalSize}
                  onChange={(e) => setNoGoNominalSize(e.target.value)}
                  sx={{ width: "80%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={noGoMax}
                  onChange={(e) => setNoGoMax(e.target.value)}
                  sx={{ width: "80%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={noGoMin}
                  onChange={(e) => setNoGoMin(e.target.value)}
                  sx={{ width: "80%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
            </TableRow>

            {/* WEAR LIMIT */}
            <TableRow>
              <TableCell sx={{ fontWeight: "600" }}>WEAR LIMIT</TableCell>
              <TableCell align="center" colSpan={3}>
                <TextField
                  type="number"
                  size="small"
                  value={wearLimit}
                  onChange={(e) => setWearLimit(e.target.value)}
                  sx={{ width: "30%" }}
                  inputProps={{ step: "any" }}
                  placeholder="Wear Limit (mm)"
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Bottom Action Toolbar */}
      <Toolbar
        style={{
          padding: "0px",
          overflow: "auto",
          display: "flex",
          justifyContent: "flex-end",
          gap: "20px",
          marginTop: "3rem",
        }}
      >
        <Button
          variant="contained"
          style={{ backgroundColor: "grey" }}
          size="small"
          onClick={() => {
            if (window.confirm("Are you sure you want to cancel?")) {
              window.history.back();
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          color="primary"
          onClick={handleSubmit}
        >
          {isEditing ? "Update" : "Save"}
        </Button>
      </Toolbar>
      <ToastContainer />
    </EditWrap>
  );
}
